import { quoteQuery } from "@common/api/quoting/quote"
import { useDeleteQuotePhaseMutation } from "@common/api/quoting/quote-phase"
import { currentUserQuery } from "@common/api/users"
import { ConfirmDialog } from "@common/components/shared/confirm-dialog"
import { hasPermission } from "@common/lib/permissions"
import { handleError } from "@common/utils"
import { useSuspenseQuery } from "@tanstack/react-query"
import { createFileRoute, redirect, useNavigate } from "@tanstack/react-router"
import useCustomToast from "@/hooks/use-custom-toast"

export const Route = createFileRoute(
  "/_layout/quotes_/$quoteId/phases/$phaseId/delete",
)({
  beforeLoad: async ({ context: { queryClient }, params, search }) => {
    const me = await queryClient.ensureQueryData(currentUserQuery())
    if (!hasPermission(me, "quote:update")) {
      throw redirect({
        to: "/quotes/$quoteId",
        params: { quoteId: params.quoteId },
        search,
      })
    }
  },
  component: DeletePhaseDialog,
})

function DeletePhaseDialog(): React.ReactElement {
  const { quoteId, phaseId } = Route.useParams()
  const { data: quote } = useSuspenseQuery(quoteQuery({ quote_id: quoteId }))
  const navigate = useNavigate()
  const showToast = useCustomToast()
  const mutation = useDeleteQuotePhaseMutation()
  const phase = quote.active_phases.find(({ id }) => id === phaseId)
  const close = () =>
    navigate({ to: "/quotes/$quoteId", params: { quoteId }, search: true })

  return (
    <ConfirmDialog
      title={`Archive ${phase?.name ?? "this phase"}?`}
      description="Its tasks stay on the quote, without a phase."
      action="Archive"
      tone="default"
      pendingAction="Archiving…"
      pending={mutation.isPending}
      onConfirm={() =>
        mutation.mutate(
          { quote_id: quoteId, phase_id: phaseId },
          {
            onSuccess: () => {
              showToast("Archived", "Phase archived.", "success")
              close()
            },
            onError: (error) => handleError(error, showToast),
          },
        )
      }
      onCancel={close}
    />
  )
}
